<template>
  <div class="unified-student-upload">
    <PageSpinner :onloader="processing || onloader === 'flex' ? 'flex' : 'none'" />

    <!-- 1. Mandatory Class, Option, and Arm Selection Bar -->
    <div class="card mb-3 border-0 shadow-sm" style="background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);">
      <div class="card-body py-3">
        <div class="d-flex align-items-center justify-content-between mb-2 flex-wrap">
          <h5 class="mb-0 text-primary font-weight-bold">
            <i class="fa fa-layer-group mr-2"></i>1. Select Class, Option, and Arm
          </h5>
          <span v-if="isClassOptionArmSelected" class="badge badge-success px-3 py-2">
            <i class="fa fa-check-circle mr-1"></i>Target Assigned: {{ selectedClassLabel }} &bull; {{ selectedOptionLabel }} &bull; {{ selectedArmLabel }}
          </span>
          <span v-else class="badge badge-warning px-3 py-2">
            <i class="fa fa-info-circle mr-1"></i>Required before adding students
          </span>
        </div>
        <p class="text-muted small mb-3">
          Select the target Class, Option, and Arm below. These values apply to all student records submitted during this session.
        </p>

        <div class="row">
          <div class="col-md-4">
            <div class="form-group mb-2">
              <label class="form-control-label font-weight-bold text-dark">
                Class <span class="text-danger">*</span>
              </label>
              <select
                v-model="selectedClassId"
                @change="onClassChange"
                class="form-control form-control-sm"
                :disabled="processing"
              >
                <option :value="0">-- Select Class --</option>
                <option v-for="item in schclass" :key="item.Id" :value="item.Id">
                  {{ item.Sch_Class }}
                </option>
              </select>
            </div>
          </div>

          <div class="col-md-4">
            <div class="form-group mb-2">
              <label class="form-control-label font-weight-bold text-dark">
                Option <span class="text-danger">*</span>
              </label>
              <select
                v-model="selectedOptionId"
                @change="onOptionChange"
                class="form-control form-control-sm"
                :disabled="processing || !selectedClassId || selectedClassId === 0"
              >
                <option :value="0">-- Select Option --</option>
                <option v-for="item in localOptionItems" :key="item.Id" :value="item.Id">
                  {{ item.ClassOption }}
                </option>
              </select>
            </div>
          </div>

          <div class="col-md-4">
            <div class="form-group mb-2">
              <label class="form-control-label font-weight-bold text-dark">
                Arm <span class="text-danger">*</span>
              </label>
              <select
                v-model="selectedArmId"
                class="form-control form-control-sm"
                :disabled="processing || !selectedOptionId || selectedOptionId === 0"
              >
                <option :value="0">-- Select Arm --</option>
                <option v-for="item in localArmItems" :key="item.Id" :value="item.Id">
                  {{ item.SectionName }}
                </option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 2. Mode Selector (Manual Entry vs Excel Bulk Upload) -->
    <div class="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2 flex-wrap">
      <ul class="nav nav-pills" role="tablist">
        <li class="nav-item">
          <a
            class="nav-link py-2 px-4"
            :class="{ active: uploadMode === 'manual', disabled: !isClassOptionArmSelected }"
            href="#"
            @click.prevent="switchMode('manual')"
          >
            <i class="fa fa-user-plus mr-2"></i>Manual Entry
          </a>
        </li>
        <li class="nav-item">
          <a
            class="nav-link py-2 px-4"
            :class="{ active: uploadMode === 'excel', disabled: !isClassOptionArmSelected }"
            href="#"
            @click.prevent="switchMode('excel')"
          >
            <i class="fa fa-file-excel mr-2"></i>Excel Bulk Upload
          </a>
        </li>
      </ul>

      <div class="mt-2 mt-md-0" v-if="!isClassOptionArmSelected">
        <span class="text-danger small font-weight-bold">
          <i class="fa fa-exclamation-triangle mr-1"></i>Please select Class, Option, and Arm first to enable entry.
        </span>
      </div>
    </div>

    <!-- Locked State Prompt -->
    <div v-if="!isClassOptionArmSelected" class="text-center py-5 border rounded bg-white shadow-sm my-3">
      <div class="icon icon-shape bg-gradient-primary text-white rounded-circle shadow mb-3 mx-auto" style="width: 64px; height: 64px; display: flex; align-items: center; justify-content: center; font-size: 24px;">
        <i class="fa fa-graduation-cap"></i>
      </div>
      <h4 class="text-dark">Select Class, Option, and Arm to Proceed</h4>
      <p class="text-muted mb-0" style="max-width: 500px; margin: 0 auto;">
        Choose the class level, option, and arm in the panel above. Once selected, you can immediately enter student details manually or upload in bulk using Excel.
      </p>
    </div>

    <!-- Active Form / Bulk Container (Shown only when Class, Option, and Arm are selected) -->
    <div v-else>
      <!-- ================= MODE 1: MANUAL SINGLE ENTRY ================= -->
      <div v-if="uploadMode === 'manual'" class="card border-0 shadow-sm">
        <div class="card-header bg-transparent d-flex justify-content-between align-items-center py-3 flex-wrap">
          <div>
            <h5 class="mb-0 text-primary">
              <i class="fa fa-user-edit mr-2"></i>Single Student Details Entry
            </h5>
            <small class="text-muted">
              Entering student for <strong>{{ selectedClassLabel }} &bull; {{ selectedOptionLabel }} &bull; {{ selectedArmLabel }}</strong>
            </small>
          </div>
          <div>
            <span class="badge badge-light border px-2 py-1">
              Admission No: <strong>{{ manualForm.AdmissionNo || 'Not Generated' }}</strong>
            </span>
          </div>
        </div>

        <div class="card-body">
          <form role="form" @submit.prevent="submitSingleStudent(false)">
            <!-- Biodata Section -->
            <div class="card mb-3 border">
              <div class="card-header bg-light py-2">
                <h6 class="mb-0 text-dark font-weight-bold">Biodata</h6>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-12 pb-3">
                    <div class="row align-items-center">
                      <div class="col-sm-12 col-md-4 text-center mb-2 mb-md-0">
                        <img
                          v-if="manualForm.Passport"
                          :src="manualForm.Passport"
                          class="img-fluid rounded border shadow-sm"
                          style="max-height: 120px;"
                          alt="Student Passport"
                        />
                        <img
                          v-else
                          src="/img/brand/empty.png"
                          class="img-fluid rounded border shadow-sm"
                          style="max-height: 120px;"
                          alt="Empty Passport"
                        />
                      </div>
                      <div class="col-sm-12 col-md-8">
                        <label class="font-weight-bold small text-muted">Upload Passport Photo (Optional)</label>
                        <ImageUploader :existingPath="manualForm.Passport || ''" @emitOutImgPathval="onManualPassportUpload" />
                      </div>
                    </div>
                  </div>

                  <div class="col-sm-4">
                    <div class="form-group mb-2">
                      <label class="form-control-label small">Surname <span class="text-danger">*</span></label>
                      <input
                        v-model.trim="manualForm.LastName"
                        class="form-control form-control-sm"
                        :class="{'is-invalid': manualErrors.LastName}"
                        placeholder="e.g. Ogunleye"
                        required
                      />
                      <small v-if="manualErrors.LastName" class="text-danger">{{ manualErrors.LastName }}</small>
                    </div>
                  </div>

                  <div class="col-sm-4">
                    <div class="form-group mb-2">
                      <label class="form-control-label small">First Name <span class="text-danger">*</span></label>
                      <input
                        v-model.trim="manualForm.FirstName"
                        class="form-control form-control-sm"
                        :class="{'is-invalid': manualErrors.FirstName}"
                        placeholder="e.g. Adedamola"
                        required
                      />
                      <small v-if="manualErrors.FirstName" class="text-danger">{{ manualErrors.FirstName }}</small>
                    </div>
                  </div>

                  <div class="col-sm-4">
                    <div class="form-group mb-2">
                      <label class="form-control-label small">Other Name</label>
                      <input
                        v-model.trim="manualForm.MiddleName"
                        class="form-control form-control-sm"
                        placeholder="e.g. Emmanuel"
                      />
                    </div>
                  </div>

                  <div class="col-sm-4">
                    <div class="form-group mb-2">
                      <label class="form-control-label small">Sex <span class="text-danger">*</span></label>
                      <select
                        v-model="manualForm.SexId"
                        class="form-control form-control-sm"
                        :class="{'is-invalid': manualErrors.SexId}"
                        required
                      >
                        <option value="0">Select...</option>
                        <option value="10">Male</option>
                        <option value="11">Female</option>
                      </select>
                      <small v-if="manualErrors.SexId" class="text-danger">{{ manualErrors.SexId }}</small>
                    </div>
                  </div>

                  <div class="col-sm-4">
                    <div class="form-group mb-2">
                      <label class="form-control-label small">Date of Birth</label>
                      <input
                        type="date"
                        v-model="manualForm.DOB"
                        :max="todayDate"
                        class="form-control form-control-sm"
                      />
                    </div>
                  </div>

                  <div class="col-sm-4">
                    <div class="form-group mb-2">
                      <label class="form-control-label small">Admission Date</label>
                      <input
                        type="date"
                        v-model="manualForm.Admissiondate"
                        class="form-control form-control-sm"
                      />
                    </div>
                  </div>

                  <div class="col-sm-4">
                    <div class="form-group mb-2">
                      <label class="form-control-label small">Admission Number <span class="text-danger">*</span></label>
                      <div class="input-group input-group-sm">
                        <input
                          v-model.trim="manualForm.AdmissionNo"
                          class="form-control"
                          :class="{'is-invalid': manualErrors.AdmissionNo}"
                          placeholder="Admission Number"
                          required
                        />
                        <div class="input-group-append">
                          <button
                            class="btn btn-outline-primary"
                            type="button"
                            @click="fetchNextAdmissionNo"
                            title="Auto generate admission number"
                          >
                            <i class="fa fa-sync-alt"></i>
                          </button>
                        </div>
                      </div>
                      <small v-if="manualErrors.AdmissionNo" class="text-danger">{{ manualErrors.AdmissionNo }}</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Guardian Section -->
            <div class="card mb-3 border">
              <div class="card-header bg-light py-2">
                <h6 class="mb-0 text-dark font-weight-bold">Guardian & Contact Information</h6>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-sm-4">
                    <div class="form-group mb-2">
                      <label class="form-control-label small">Guardian Surname</label>
                      <input
                        v-model.trim="manualForm.GLastName"
                        class="form-control form-control-sm"
                        placeholder="Guardian Surname"
                      />
                    </div>
                  </div>

                  <div class="col-sm-4">
                    <div class="form-group mb-2">
                      <label class="form-control-label small">Guardian First Name</label>
                      <input
                        v-model.trim="manualForm.GFirstname"
                        class="form-control form-control-sm"
                        placeholder="Guardian First Name"
                      />
                    </div>
                  </div>

                  <div class="col-sm-4">
                    <div class="form-group mb-2">
                      <label class="form-control-label small">Guardian Other Name</label>
                      <input
                        v-model.trim="manualForm.GMiddlename"
                        class="form-control form-control-sm"
                        placeholder="Guardian Other Name"
                      />
                    </div>
                  </div>

                  <div class="col-sm-4">
                    <div class="form-group mb-2">
                      <label class="form-control-label small">Guardian Phone Number <span class="text-danger">*</span></label>
                      <input
                        v-model.trim="manualForm.GPhoneNo"
                        class="form-control form-control-sm"
                        :class="{'is-invalid': manualErrors.GPhoneNo}"
                        placeholder="e.g. 08012345678"
                        required
                      />
                      <small v-if="manualErrors.GPhoneNo" class="text-danger">{{ manualErrors.GPhoneNo }}</small>
                    </div>
                  </div>

                  <div class="col-sm-4">
                    <div class="form-group mb-2">
                      <label class="form-control-label small">Guardian Email Address</label>
                      <input
                        type="email"
                        v-model.trim="manualForm.GEmail"
                        class="form-control form-control-sm"
                        placeholder="guardian@example.com"
                      />
                    </div>
                  </div>

                  <div class="col-md-12">
                    <div class="form-group mb-1">
                      <label class="form-control-label small">Full Address <span class="text-danger">*</span></label>
                      <textarea
                        v-model.trim="manualForm.FullAddress"
                        class="form-control form-control-sm"
                        :class="{'is-invalid': manualErrors.FullAddress}"
                        rows="2"
                        placeholder="Street, City, State"
                        required
                      ></textarea>
                      <small v-if="manualErrors.FullAddress" class="text-danger">{{ manualErrors.FullAddress }}</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Footer Actions -->
            <div class="d-flex justify-content-between align-items-center flex-wrap pt-2">
              <button
                type="button"
                class="btn btn-outline-secondary btn-sm"
                @click="resetManualForm(true)"
                :disabled="processing"
              >
                <i class="fa fa-undo mr-1"></i>Reset Form
              </button>

              <div>
                <button
                  type="button"
                  class="btn btn-primary btn-sm mr-2"
                  @click="submitSingleStudent(true)"
                  :disabled="processing"
                >
                  <i class="fa fa-user-plus mr-1"></i>Save &amp; Add Another
                </button>
                <button
                  type="submit"
                  class="btn btn-success btn-sm px-4"
                  :disabled="processing"
                >
                  <i class="fa fa-check mr-1"></i>Save Student
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      <!-- ================= MODE 2: EXCEL BULK UPLOAD ================= -->
      <div v-else-if="uploadMode === 'excel'">
        <!-- File Upload Area -->
        <div class="card mb-3 border-0 shadow-sm bg-secondary">
          <div class="card-body py-3">
            <div class="row align-items-center">
              <div class="col-md-7 mb-3 mb-md-0">
                <h5 class="mb-1 text-primary">
                  <i class="fa fa-file-excel mr-2"></i>Upload Excel File (.xlsx, .xls, .csv)
                </h5>
                <small class="text-muted d-block mb-2">
                  Target Class: <strong>{{ selectedClassLabel }}</strong> &bull; Option: <strong>{{ selectedOptionLabel }}</strong> &bull; Arm: <strong>{{ selectedArmLabel }}</strong>.
                  These selections will be attached automatically to all imported students.
                </small>

                <base-file-upload
                  ref="excelUploadComp"
                  class="mb-2"
                  accept=".xlsx, .xls, .csv"
                  icon="fa fa-file-excel"
                  title="Choose Student Excel/CSV file or drag & drop here"
                  subtitle="Auto-maps student admission, names, sex, guardian, and address fields"
                  button-text="Browse File"
                  :disabled="processing"
                  :selected-name="selectedFileName"
                  @change="handleFileUpload"
                  @clear="resetBulkUpload"
                />
              </div>

              <div class="col-md-5 text-md-right">
                <button
                  type="button"
                  class="btn btn-outline-info btn-sm mr-2 mb-2"
                  @click="downloadTemplate"
                  :disabled="processing"
                  title="Download sample Excel template"
                >
                  <i class="fa fa-download mr-1"></i>Sample Template
                </button>
                <button
                  type="button"
                  class="btn btn-outline-danger btn-sm mb-2"
                  @click="resetBulkUpload"
                  :disabled="processing || rows.length === 0"
                  title="Clear all imported records"
                >
                  <i class="fa fa-trash mr-1"></i>Reset List
                </button>
              </div>
            </div>

            <div v-if="parseError" class="alert alert-danger mb-0 mt-2 py-2">
              <i class="fa fa-exclamation-triangle mr-2"></i>{{ parseError }}
            </div>
          </div>
        </div>

        <!-- Processing & Progress Tracking Card -->
        <div v-if="processing || hasRunOnce" class="card mb-3 border-0 shadow-sm">
          <div class="card-body py-3">
            <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap">
              <h5 class="mb-0">
                <i class="fa fa-tasks mr-2 text-primary"></i>Background Sequential Processing
              </h5>
              <span class="badge" :class="processing ? 'badge-primary' : (failedCount > 0 ? 'badge-warning' : 'badge-success')">
                {{ processing ? 'Processing In Progress...' : (failedCount > 0 ? 'Processing Completed with Errors' : 'Processing Completed Successfully') }}
              </span>
            </div>

            <!-- Progress bar -->
            <div class="progress my-2" style="height: 16px;">
              <div
                class="progress-bar progress-bar-striped"
                :class="processing ? 'progress-bar-animated bg-primary' : 'bg-success'"
                role="progressbar"
                :style="{ width: progressPercentage + '%' }"
                :aria-valuenow="progressPercentage"
                aria-valuemin="0"
                aria-valuemax="100"
              >
                {{ progressPercentage }}%
              </div>
            </div>

            <!-- Metric Badges -->
            <div class="d-flex flex-wrap mt-2 font-weight-bold">
              <div class="badge badge-light border p-2 mr-2 mb-2">
                Total Records: <span class="text-dark">{{ rows.length }}</span>
              </div>
              <div class="badge badge-info p-2 mr-2 mb-2">
                Processed: <span>{{ processedCount }} / {{ rows.length }}</span>
              </div>
              <div class="badge badge-success p-2 mr-2 mb-2">
                <i class="fa fa-check mr-1"></i>Success: <span>{{ successCount }}</span>
              </div>
              <div class="badge badge-danger p-2 mr-2 mb-2">
                <i class="fa fa-times mr-1"></i>Failed: <span>{{ failedCount }}</span>
              </div>
              <div class="badge badge-warning p-2 mr-2 mb-2" v-if="duplicateAdmissionCount > 0">
                <i class="fa fa-copy mr-1"></i>Duplicate Adm No: <span>{{ duplicateAdmissionCount }}</span>
              </div>
            </div>

            <div v-if="processing" class="text-muted small mt-1">
              <i class="fa fa-spinner fa-spin mr-1"></i> Submitting record {{ currentIndex + 1 }} of {{ rows.length }}...
            </div>
          </div>
        </div>

        <!-- Editable Data Table Section (Class, Option, Arm EXCLUDED from columns) -->
        <div v-if="rows.length > 0" class="card border-0 shadow-sm mb-3">
          <div class="card-header bg-transparent d-flex justify-content-between align-items-center py-2 flex-wrap">
            <div class="mb-2 mb-md-0">
              <h5 class="mb-0">
                <i class="fa fa-table mr-2 text-primary"></i>Editable Imported Records ({{ rows.length }})
              </h5>
              <small class="text-muted">
                Class, Option, and Arm are excluded from columns and automatically assigned to <strong>{{ selectedClassLabel }} &bull; {{ selectedOptionLabel }} &bull; {{ selectedArmLabel }}</strong>.
              </small>
            </div>

            <div class="d-flex align-items-center flex-wrap my-1">
              <!-- View mode switcher: Table vs Cards -->
              <div class="btn-group btn-group-sm mr-2 mb-1" role="group">
                <button
                  type="button"
                  class="btn"
                  :class="effectiveViewMode === 'table' ? 'btn-primary' : 'btn-outline-primary'"
                  @click="viewMode = 'table'"
                  title="Table view"
                >
                  <i class="fa fa-table mr-1"></i>Table
                </button>
                <button
                  type="button"
                  class="btn"
                  :class="effectiveViewMode === 'card' ? 'btn-primary' : 'btn-outline-primary'"
                  @click="viewMode = 'card'"
                  title="Card view (optimized for mobile)"
                >
                  <i class="fa fa-th-large mr-1"></i>Cards
                </button>
              </div>

              <button
                type="button"
                class="btn btn-sm btn-outline-primary mr-2 mb-1"
                @click="addNewRow"
                :disabled="processing"
              >
                <i class="fa fa-plus mr-1"></i><span class="d-none d-sm-inline">Add </span>Row
              </button>
              <button
                type="button"
                class="btn btn-sm btn-outline-warning mr-2 mb-1"
                @click="filterErrorsOnly = !filterErrorsOnly"
                v-if="hasValidationErrors || failedCount > 0"
              >
                <i class="fa fa-filter mr-1"></i>{{ filterErrorsOnly ? 'All' : 'Issues Only' }}
              </button>
              <button
                type="button"
                class="btn btn-sm btn-warning mr-2 mb-1"
                @click="retryFailed"
                :disabled="processing || failedCount === 0"
                v-if="failedCount > 0"
              >
                <i class="fa fa-redo mr-1"></i>Retry ({{ failedCount }})
              </button>
              <button
                type="button"
                class="btn btn-sm btn-success px-3 mb-1"
                @click="startBulkSubmission"
                :disabled="processing || pendingOrFailedCount === 0 || hasValidationErrors"
              >
                <i class="fa fa-cloud-upload-alt mr-1"></i>
                {{ processing ? 'Submitting...' : `Submit (${pendingOrFailedCount})` }}
              </button>
            </div>
          </div>

          <!-- DESKTOP / TABLE VIEW -->
          <div v-if="effectiveViewMode === 'table'" class="table-responsive bulk-table-wrapper" style="max-height: 520px; overflow-y: auto;">
            <table class="table table-sm table-hover align-items-center mb-0 bulk-table">
              <thead class="thead-light sticky-top">
                <tr>
                  <th style="width: 45px;">#</th>
                  <th style="width: 100px;">Status</th>
                  <th style="min-width: 140px;">Adm No *</th>
                  <th style="min-width: 140px;">Surname *</th>
                  <th style="min-width: 140px;">First Name *</th>
                  <th style="min-width: 130px;">Other Name</th>
                  <th style="min-width: 110px;">Sex *</th>
                  <th style="min-width: 130px;">DOB</th>
                  <th style="min-width: 140px;">Guardian Surname</th>
                  <th style="min-width: 140px;">Guardian First Name</th>
                  <th style="min-width: 130px;">Guardian Other</th>
                  <th style="min-width: 150px;">Guardian Phone *</th>
                  <th style="min-width: 160px;">Guardian Email</th>
                  <th style="min-width: 180px;">Full Address *</th>
                  <th style="width: 60px;" class="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in visibleRows"
                  :key="row._rowId"
                  :class="{
                    'table-danger-light': row._errors && row._errors.length > 0,
                    'table-success-light': row.status === 'success',
                    'table-info-light': row.status === 'processing'
                  }"
                >
                  <td class="font-weight-bold text-muted text-center align-middle">
                    {{ row._originalIndex + 1 }}
                  </td>

                  <!-- Status -->
                  <td class="align-middle">
                    <span v-if="row.status === 'success'" class="badge badge-success">
                      <i class="fa fa-check mr-1"></i>Uploaded
                    </span>
                    <span v-else-if="row.status === 'failed'" class="badge badge-danger" :title="row.errorMsg">
                      <i class="fa fa-times mr-1"></i>Failed
                    </span>
                    <span v-else-if="row.status === 'processing'" class="badge badge-primary">
                      <i class="fa fa-spinner fa-spin mr-1"></i>Saving
                    </span>
                    <span v-else-if="row._errors && row._errors.length > 0" class="badge badge-warning" :title="row._errors.join('; ')">
                      <i class="fa fa-exclamation-triangle mr-1"></i>Invalid
                    </span>
                    <span v-else class="badge badge-light border">
                      Ready
                    </span>
                    <div v-if="row.errorMsg" class="text-danger small mt-1" style="max-width: 120px; font-size: 11px; line-height: 1.2;">
                      {{ row.errorMsg }}
                    </div>
                  </td>

                  <!-- Admission No -->
                  <td class="align-middle">
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      :class="{'is-invalid': fieldHasError(row, 'AdmissionNo')}"
                      v-model.trim="row.AdmissionNo"
                      :disabled="processing || row.status === 'success'"
                      placeholder="Adm No *"
                      @input="validateAllBulkRows"
                    />
                  </td>

                  <!-- Surname -->
                  <td class="align-middle">
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      :class="{'is-invalid': fieldHasError(row, 'LastName')}"
                      v-model.trim="row.LastName"
                      :disabled="processing || row.status === 'success'"
                      placeholder="Surname *"
                      @input="validateAllBulkRows"
                    />
                  </td>

                  <!-- First Name -->
                  <td class="align-middle">
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      :class="{'is-invalid': fieldHasError(row, 'FirstName')}"
                      v-model.trim="row.FirstName"
                      :disabled="processing || row.status === 'success'"
                      placeholder="First Name *"
                      @input="validateAllBulkRows"
                    />
                  </td>

                  <!-- Other Name -->
                  <td class="align-middle">
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      v-model.trim="row.MiddleName"
                      :disabled="processing || row.status === 'success'"
                      placeholder="Middle Name"
                    />
                  </td>

                  <!-- Sex -->
                  <td class="align-middle">
                    <select
                      class="form-control form-control-sm"
                      :class="{'is-invalid': fieldHasError(row, 'SexId')}"
                      v-model="row.SexId"
                      :disabled="processing || row.status === 'success'"
                      @change="validateAllBulkRows"
                    >
                      <option value="0">Select...</option>
                      <option value="10">Male</option>
                      <option value="11">Female</option>
                    </select>
                  </td>

                  <!-- DOB -->
                  <td class="align-middle">
                    <input
                      type="date"
                      class="form-control form-control-sm"
                      v-model="row.DOB"
                      :max="todayDate"
                      :disabled="processing || row.status === 'success'"
                    />
                  </td>

                  <!-- Guardian Surname -->
                  <td class="align-middle">
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      v-model.trim="row.GLastName"
                      :disabled="processing || row.status === 'success'"
                      placeholder="G. Surname"
                    />
                  </td>

                  <!-- Guardian First Name -->
                  <td class="align-middle">
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      v-model.trim="row.GFirstname"
                      :disabled="processing || row.status === 'success'"
                      placeholder="G. First Name"
                    />
                  </td>

                  <!-- Guardian Other Name -->
                  <td class="align-middle">
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      v-model.trim="row.GMiddlename"
                      :disabled="processing || row.status === 'success'"
                      placeholder="G. Middle"
                    />
                  </td>

                  <!-- Guardian Phone -->
                  <td class="align-middle">
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      :class="{'is-invalid': fieldHasError(row, 'GPhoneNo')}"
                      v-model.trim="row.GPhoneNo"
                      :disabled="processing || row.status === 'success'"
                      placeholder="Phone *"
                      @input="validateAllBulkRows"
                    />
                  </td>

                  <!-- Guardian Email -->
                  <td class="align-middle">
                    <input
                      type="email"
                      class="form-control form-control-sm"
                      v-model.trim="row.GEmail"
                      :disabled="processing || row.status === 'success'"
                      placeholder="Email"
                    />
                  </td>

                  <!-- Full Address -->
                  <td class="align-middle">
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      :class="{'is-invalid': fieldHasError(row, 'FullAddress')}"
                      v-model.trim="row.FullAddress"
                      :disabled="processing || row.status === 'success'"
                      placeholder="Full Address *"
                      @input="validateAllBulkRows"
                    />
                  </td>

                  <!-- Remove Action -->
                  <td class="align-middle text-center">
                    <button
                      type="button"
                      class="btn btn-outline-danger btn-sm p-1"
                      style="width: 28px; height: 28px; line-height: 1;"
                      @click="removeRow(row._originalIndex)"
                      :disabled="processing || row.status === 'processing'"
                      title="Remove row"
                    >
                      <i class="fa fa-trash"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- MOBILE / CARD VIEW -->
          <div v-else class="mobile-cards-container p-2" style="max-height: 540px; overflow-y: auto;">
            <div
              v-for="row in visibleRows"
              :key="row._rowId"
              class="card mb-3 border shadow-sm mobile-record-card"
              :class="{
                'border-danger': row._errors && row._errors.length > 0,
                'border-success': row.status === 'success',
                'border-primary': row.status === 'processing'
              }"
            >
              <!-- Card Header: Index, Status & Delete button -->
              <div class="card-header py-2 px-3 bg-light d-flex justify-content-between align-items-center">
                <div class="d-flex align-items-center">
                  <span class="badge badge-dark mr-2">#{{ row._originalIndex + 1 }}</span>
                  <span v-if="row.status === 'success'" class="badge badge-success">
                    <i class="fa fa-check mr-1"></i>Uploaded
                  </span>
                  <span v-else-if="row.status === 'failed'" class="badge badge-danger" :title="row.errorMsg">
                    <i class="fa fa-times mr-1"></i>Failed
                  </span>
                  <span v-else-if="row.status === 'processing'" class="badge badge-primary">
                    <i class="fa fa-spinner fa-spin mr-1"></i>Saving
                  </span>
                  <span v-else-if="row._errors && row._errors.length > 0" class="badge badge-warning" :title="row._errors.join('; ')">
                    <i class="fa fa-exclamation-triangle mr-1"></i>{{ row._errors.length }} Issue(s)
                  </span>
                  <span v-else class="badge badge-light border">
                    Ready
                  </span>
                </div>

                <button
                  type="button"
                  class="btn btn-outline-danger btn-sm py-1 px-2"
                  @click="removeRow(row._originalIndex)"
                  :disabled="processing || row.status === 'processing'"
                  title="Remove row"
                >
                  <i class="fa fa-trash mr-1"></i>Delete
                </button>
              </div>

              <!-- Card Body: Form Fields Stacked in responsive grid -->
              <div class="card-body p-3">
                <div v-if="row.errorMsg" class="alert alert-danger py-1 px-2 mb-2 small">
                  <i class="fa fa-exclamation-circle mr-1"></i>{{ row.errorMsg }}
                </div>
                <div v-if="row._errors && row._errors.length > 0" class="alert alert-warning py-1 px-2 mb-2 small">
                  <ul class="mb-0 pl-3">
                    <li v-for="(err, eIdx) in row._errors" :key="eIdx">{{ err }}</li>
                  </ul>
                </div>

                <div class="row">
                  <!-- Admission No -->
                  <div class="col-12 col-sm-6 mb-2">
                    <label class="form-control-label small text-muted font-weight-bold mb-1">Admission No *</label>
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      :class="{'is-invalid': fieldHasError(row, 'AdmissionNo')}"
                      v-model.trim="row.AdmissionNo"
                      :disabled="processing || row.status === 'success'"
                      placeholder="Adm No *"
                      @input="validateAllBulkRows"
                    />
                  </div>

                  <!-- Surname -->
                  <div class="col-6 col-sm-6 mb-2">
                    <label class="form-control-label small text-muted font-weight-bold mb-1">Surname *</label>
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      :class="{'is-invalid': fieldHasError(row, 'LastName')}"
                      v-model.trim="row.LastName"
                      :disabled="processing || row.status === 'success'"
                      placeholder="Surname *"
                      @input="validateAllBulkRows"
                    />
                  </div>

                  <!-- First Name -->
                  <div class="col-6 col-sm-6 mb-2">
                    <label class="form-control-label small text-muted font-weight-bold mb-1">First Name *</label>
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      :class="{'is-invalid': fieldHasError(row, 'FirstName')}"
                      v-model.trim="row.FirstName"
                      :disabled="processing || row.status === 'success'"
                      placeholder="First Name *"
                      @input="validateAllBulkRows"
                    />
                  </div>

                  <!-- Other Name -->
                  <div class="col-6 col-sm-6 mb-2">
                    <label class="form-control-label small text-muted font-weight-bold mb-1">Other Name</label>
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      v-model.trim="row.MiddleName"
                      :disabled="processing || row.status === 'success'"
                      placeholder="Middle Name"
                    />
                  </div>

                  <!-- Sex -->
                  <div class="col-6 col-sm-6 mb-2">
                    <label class="form-control-label small text-muted font-weight-bold mb-1">Sex *</label>
                    <select
                      class="form-control form-control-sm"
                      :class="{'is-invalid': fieldHasError(row, 'SexId')}"
                      v-model="row.SexId"
                      :disabled="processing || row.status === 'success'"
                      @change="validateAllBulkRows"
                    >
                      <option value="0">Select...</option>
                      <option value="10">Male</option>
                      <option value="11">Female</option>
                    </select>
                  </div>

                  <!-- DOB -->
                  <div class="col-6 col-sm-6 mb-2">
                    <label class="form-control-label small text-muted font-weight-bold mb-1">Date of Birth</label>
                    <input
                      type="date"
                      class="form-control form-control-sm"
                      v-model="row.DOB"
                      :max="todayDate"
                      :disabled="processing || row.status === 'success'"
                    />
                  </div>

                  <!-- Guardian Sub-Header -->
                  <div class="col-12 mt-2 mb-1">
                    <h6 class="text-primary small font-weight-bold mb-1 border-bottom pb-1">
                      <i class="fa fa-user-shield mr-1"></i>Guardian &amp; Contact Details
                    </h6>
                  </div>

                  <!-- Guardian Surname -->
                  <div class="col-6 col-sm-4 mb-2">
                    <label class="form-control-label small text-muted font-weight-bold mb-1">Guardian Surname</label>
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      v-model.trim="row.GLastName"
                      :disabled="processing || row.status === 'success'"
                      placeholder="G. Surname"
                    />
                  </div>

                  <!-- Guardian First Name -->
                  <div class="col-6 col-sm-4 mb-2">
                    <label class="form-control-label small text-muted font-weight-bold mb-1">Guardian First Name</label>
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      v-model.trim="row.GFirstname"
                      :disabled="processing || row.status === 'success'"
                      placeholder="G. First Name"
                    />
                  </div>

                  <!-- Guardian Other Name -->
                  <div class="col-12 col-sm-4 mb-2">
                    <label class="form-control-label small text-muted font-weight-bold mb-1">Guardian Other Name</label>
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      v-model.trim="row.GMiddlename"
                      :disabled="processing || row.status === 'success'"
                      placeholder="G. Middle"
                    />
                  </div>

                  <!-- Guardian Phone -->
                  <div class="col-12 col-sm-6 mb-2">
                    <label class="form-control-label small text-muted font-weight-bold mb-1">Guardian Phone *</label>
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      :class="{'is-invalid': fieldHasError(row, 'GPhoneNo')}"
                      v-model.trim="row.GPhoneNo"
                      :disabled="processing || row.status === 'success'"
                      placeholder="08012345678"
                      @input="validateAllBulkRows"
                    />
                  </div>

                  <!-- Guardian Email -->
                  <div class="col-12 col-sm-6 mb-2">
                    <label class="form-control-label small text-muted font-weight-bold mb-1">Guardian Email</label>
                    <input
                      type="email"
                      class="form-control form-control-sm"
                      v-model.trim="row.GEmail"
                      :disabled="processing || row.status === 'success'"
                      placeholder="guardian@example.com"
                    />
                  </div>

                  <!-- Full Address -->
                  <div class="col-12 mb-1">
                    <label class="form-control-label small text-muted font-weight-bold mb-1">Full Address *</label>
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      :class="{'is-invalid': fieldHasError(row, 'FullAddress')}"
                      v-model.trim="row.FullAddress"
                      :disabled="processing || row.status === 'success'"
                      placeholder="Street, City, State"
                      @input="validateAllBulkRows"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="card-footer py-2 bg-light d-flex justify-content-between align-items-center flex-wrap">
            <small class="text-muted mb-2 mb-md-0">
              Showing {{ visibleRows.length }} of {{ rows.length }} records.
              <span v-if="hasValidationErrors" class="text-danger font-weight-bold ml-1 d-block d-sm-inline">
                Resolve {{ totalErrorCount }} error(s) before submit.
              </span>
            </small>
            <div class="d-flex align-items-center flex-wrap">
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary mr-2"
                @click="cancel"
                :disabled="processing"
              >
                Close
              </button>
              <button
                type="button"
                class="btn btn-sm btn-success px-4"
                @click="startBulkSubmission"
                :disabled="processing || pendingOrFailedCount === 0 || hasValidationErrors"
              >
                <i class="fa fa-cloud-upload-alt mr-1"></i>
                {{ processing ? 'Processing...' : `Submit All (${pendingOrFailedCount})` }}
              </button>
            </div>
          </div>
        </div>

        <!-- Empty state when no Excel file imported -->
        <div v-else class="text-center py-5 border rounded bg-white shadow-sm mb-3">
          <i class="fa fa-file-excel fa-4x text-success mb-3"></i>
          <h4 class="text-dark">No Excel File Imported Yet</h4>
          <p class="text-muted mb-3" style="max-width: 520px; margin: 0 auto;">
            Upload an Excel file (.xlsx, .xls) or CSV above to extract and preview student records in the editable table.
          </p>
          <button
            type="button"
            class="btn btn-sm btn-outline-info mr-2"
            @click="downloadTemplate"
          >
            <i class="fa fa-download mr-1"></i>Download Template
          </button>
          <button
            type="button"
            class="btn btn-sm btn-outline-primary"
            @click="addNewRow"
          >
            <i class="fa fa-plus mr-1"></i>Add Row Manually
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import moment from "moment";
import Alert from "@/mixins/Alert.mixins";
import PageSpinner from "@/components/PageSpinner";
import ImageUploader from "@/components/ImageUploader";

export default {
  name: "UnifiedStudentUploadModal",
  mixins: [Alert],
  components: {
    PageSpinner,
    ImageUploader
  },
  props: {
    studentRepository: {
      type: Object,
      required: true
    },
    schclass: {
      type: Array,
      default: () => []
    },
    existingStudents: {
      type: Array,
      default: () => []
    },
    initialClassId: {
      type: [Number, String],
      default: 0
    },
    initialOptionId: {
      type: [Number, String],
      default: 0
    },
    initialArmId: {
      type: [Number, String],
      default: 0
    },
    initialMode: {
      type: String,
      default: "manual"
    }
  },
  data() {
    return {
      onloader: "none",
      uploadMode: this.initialMode || "manual", // 'manual' or 'excel'

      // Shared Class, Option, Arm selection
      selectedClassId: 0,
      selectedOptionId: 0,
      selectedArmId: 0,
      localOptionItems: [],
      localArmItems: [],

      // Manual Single Entry Form State
      manualForm: {
        Passport: "",
        LastName: "",
        FirstName: "",
        MiddleName: "",
        SexId: "0",
        DOB: "",
        Admissiondate: moment().format("YYYY-MM-DD"),
        AdmissionNo: "",
        GLastName: "",
        GFirstname: "",
        GMiddlename: "",
        GPhoneNo: "",
        GEmail: "",
        FullAddress: ""
      },
      manualErrors: {},

      // Excel Bulk Upload State
      selectedFileName: "",
      parseError: "",
      rows: [],
      processing: false,
      hasRunOnce: false,
      currentIndex: -1,
      filterErrorsOnly: false,
      rowCounter: 0,
      viewMode: "auto", // 'auto', 'table', or 'card'
      windowWidth: typeof window !== "undefined" ? window.innerWidth : 1024
    };
  },
  computed: {
    isMobile() {
      return this.windowWidth < 768;
    },
    effectiveViewMode() {
      if (this.viewMode === "auto") {
        return this.isMobile ? "card" : "table";
      }
      return this.viewMode;
    },
    todayDate() {
      return moment().format("YYYY-MM-DD");
    },
    isClassOptionArmSelected() {
      return (
        Number(this.selectedClassId) > 0 &&
        Number(this.selectedOptionId) > 0 &&
        Number(this.selectedArmId) > 0
      );
    },
    selectedClassLabel() {
      const found = (this.schclass || []).find(c => Number(c.Id) === Number(this.selectedClassId));
      return found ? found.Sch_Class : "Class";
    },
    selectedOptionLabel() {
      const found = (this.localOptionItems || []).find(o => Number(o.Id) === Number(this.selectedOptionId));
      return found ? found.ClassOption : "Option";
    },
    selectedArmLabel() {
      const found = (this.localArmItems || []).find(a => Number(a.Id) === Number(this.selectedArmId));
      return found ? found.SectionName : "Arm";
    },

    // Bulk counts
    processedCount() {
      return this.rows.filter(r => r.status === "success" || r.status === "failed").length;
    },
    successCount() {
      return this.rows.filter(r => r.status === "success").length;
    },
    failedCount() {
      return this.rows.filter(r => r.status === "failed").length;
    },
    pendingOrFailedCount() {
      return this.rows.filter(r => r.status !== "success").length;
    },
    progressPercentage() {
      if (this.rows.length === 0) return 0;
      return Math.round((this.processedCount / this.rows.length) * 100);
    },
    duplicateAdmissionCount() {
      const counts = {};
      this.rows.forEach(r => {
        const key = (r.AdmissionNo || "").trim().toLowerCase();
        if (key) counts[key] = (counts[key] || 0) + 1;
      });
      return Object.values(counts).filter(c => c > 1).length;
    },
    totalErrorCount() {
      return this.rows.reduce((acc, r) => acc + (r._errors ? r._errors.length : 0), 0);
    },
    hasValidationErrors() {
      return this.totalErrorCount > 0;
    },
    visibleRows() {
      const mapped = this.rows.map((row, index) => ({
        ...row,
        _originalIndex: index
      }));
      if (this.filterErrorsOnly) {
        return mapped.filter(r => (r._errors && r._errors.length > 0) || r.status === "failed");
      }
      return mapped;
    }
  },
  watch: {
    initialClassId: {
      immediate: true,
      handler(val) {
        if (val && Number(val) > 0) {
          this.selectedClassId = Number(val);
          this.fetchOptions(val, () => {
            if (this.initialOptionId) {
              this.selectedOptionId = Number(this.initialOptionId);
              this.fetchArms(this.selectedOptionId, () => {
                if (this.initialArmId) {
                  this.selectedArmId = Number(this.initialArmId);
                }
              });
            }
          });
        }
      }
    },
    initialMode: {
      immediate: true,
      handler(val) {
        if (val) this.uploadMode = val;
      }
    }
  },
  methods: {
    switchMode(mode) {
      if (!this.isClassOptionArmSelected) {
        this.ShowAlertWarning("Class, Option, and Arm Required", "Please select Class, Option, and Arm first.");
        return;
      }
      this.uploadMode = mode;
      if (mode === "manual" && !this.manualForm.AdmissionNo) {
        this.fetchNextAdmissionNo();
      }
    },

    // ----------------------------------------------------
    // CLASS, OPTION, ARM CASCADE
    // ----------------------------------------------------
    onClassChange() {
      this.selectedOptionId = 0;
      this.selectedArmId = 0;
      this.localOptionItems = [];
      this.localArmItems = [];
      if (this.selectedClassId > 0) {
        this.fetchOptions(this.selectedClassId);
      }
    },
    fetchOptions(classId, callback) {
      this.onloader = "flex";
      this.$store
        .dispatch("getoptionbyclass", classId)
        .then(resp => {
          this.localOptionItems = resp || [];
          this.onloader = "none";
          if (callback) callback();
        })
        .catch(err => {
          this.onloader = "none";
          const msg = (err.response && err.response.data) || err.message;
          this.ShowAlertError("Option Error", typeof msg === "string" ? msg : "Failed to load options");
        });
    },
    onOptionChange() {
      this.selectedArmId = 0;
      this.localArmItems = [];
      if (this.selectedOptionId > 0) {
        this.fetchArms(this.selectedOptionId);
      }
    },
    fetchArms(optionId, callback) {
      this.onloader = "flex";
      const payload = {
        Id: 1,
        ClassId: this.selectedClassId,
        OptionId: optionId,
        SchArm: "Search",
        processop: "N",
        username: this.$store.state.auth.username,
        accesskey: this.$store.state.auth.accesskey
      };
      this.$store
        .dispatch("getarmbyoption", payload)
        .then(resp => {
          this.localArmItems = resp || [];
          this.onloader = "none";
          if (callback) callback();
        })
        .catch(err => {
          this.onloader = "none";
          const msg = (err.response && err.response.data) || err.message;
          this.ShowAlertError("Arm Error", typeof msg === "string" ? msg : "Failed to load arms");
        });
    },

    // ----------------------------------------------------
    // MANUAL SINGLE ENTRY LOGIC
    // ----------------------------------------------------
    fetchNextAdmissionNo() {
      this.onloader = "flex";
      this.$store
        .dispatch("getadmno")
        .then(resp => {
          this.manualForm.AdmissionNo = resp || "";
          this.onloader = "none";
        })
        .catch(err => {
          this.onloader = "none";
          const msg = (err.response && err.response.data) || err.message;
          this.ShowAlertError(typeof msg === "string" ? msg : "Failed to fetch admission number", "");
        });
    },
    onManualPassportUpload(path) {
      this.manualForm.Passport = path || "";
    },
    validateManualForm() {
      const errs = {};
      if (!this.manualForm.LastName || !this.manualForm.LastName.trim()) {
        errs.LastName = "Surname is required";
      }
      if (!this.manualForm.FirstName || !this.manualForm.FirstName.trim()) {
        errs.FirstName = "First name is required";
      }
      if (!this.manualForm.SexId || this.manualForm.SexId === "0" || this.manualForm.SexId === 0) {
        errs.SexId = "Sex selection is required";
      }
      if (!this.manualForm.AdmissionNo || !this.manualForm.AdmissionNo.trim()) {
        errs.AdmissionNo = "Admission number is required";
      } else {
        const adm = this.manualForm.AdmissionNo.trim().toLowerCase();
        const exists = (this.existingStudents || []).some(
          s => String(s.admNo || s.AdmissionNo || "").trim().toLowerCase() === adm
        );
        if (exists) {
          errs.AdmissionNo = "Admission number already exists in system";
        }
      }
      if (!this.manualForm.GPhoneNo || !this.manualForm.GPhoneNo.trim()) {
        errs.GPhoneNo = "Guardian phone number is required";
      }
      if (!this.manualForm.FullAddress || !this.manualForm.FullAddress.trim()) {
        errs.FullAddress = "Full address is required";
      }

      this.manualErrors = errs;
      return Object.keys(errs).length === 0;
    },
    async submitSingleStudent(addAnother = false) {
      if (!this.isClassOptionArmSelected) {
        this.ShowAlertWarning("Selection Required", "Please select Class, Option, and Arm first.");
        return;
      }
      if (!this.validateManualForm()) {
        this.ShowAlertWarning("Validation Error", "Please fill in all required fields highlighted in red.");
        return;
      }

      const username = this.$store.state.auth.username;
      const accesskey = this.$store.state.auth.accesskey;

      const payload = {
        Id: 0,
        UserId: 0,
        Passport: this.manualForm.Passport || "",
        ContactId: 1,
        GContactId: 0,
        GUserId: 0,
        GPhoneNo: this.manualForm.GPhoneNo,
        GTitle: "",
        GFirstname: this.manualForm.GFirstname || "",
        GMiddlename: this.manualForm.GMiddlename || "",
        GLastName: this.manualForm.GLastName || "",
        GEmail: this.manualForm.GEmail || "",
        GId: 0,
        FullAddress: this.manualForm.FullAddress,
        FirstName: this.manualForm.FirstName,
        AdmissionNo: this.manualForm.AdmissionNo,
        Admissiondate: this.manualForm.Admissiondate || moment().format("YYYY-MM-DD"),
        ClassId: this.selectedClassId,
        OptionId: this.selectedOptionId,
        ArmId: this.selectedArmId,
        MiddleName: this.manualForm.MiddleName || "",
        LastName: this.manualForm.LastName,
        SexId: this.manualForm.SexId,
        DOB: this.manualForm.DOB || "",
        processop: "N",
        username,
        accesskey
      };

      this.onloader = "flex";
      try {
        const saved = await this.studentRepository.create(payload);
        this.onloader = "none";
        this.ShowAlertSuccess("Student Saved", "Student record created and queued for background synchronization.");
        this.$emit("student-created", saved);

        if (addAnother) {
          this.resetManualForm(false);
          this.fetchNextAdmissionNo();
        } else {
          this.resetManualForm(true);
          this.$emit("close");
        }
      } catch (err) {
        this.onloader = "none";
        const msg = (err && (err.responseMessage || err.message)) || "Submission failed";
        this.ShowAlertError("Submission Error", msg);
      }
    },
    resetManualForm(keepAdmission = false) {
      this.manualForm = {
        Passport: "",
        LastName: "",
        FirstName: "",
        MiddleName: "",
        SexId: "0",
        DOB: "",
        Admissiondate: moment().format("YYYY-MM-DD"),
        AdmissionNo: keepAdmission ? "" : this.manualForm.AdmissionNo,
        GLastName: "",
        GFirstname: "",
        GMiddlename: "",
        GPhoneNo: "",
        GEmail: "",
        FullAddress: ""
      };
      this.manualErrors = {};
    },

    // ----------------------------------------------------
    // EXCEL BULK UPLOAD LOGIC
    // ----------------------------------------------------
    handleFileUpload(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;

      this.selectedFileName = file.name;
      this.parseError = "";

      const fileExtension = file.name.split(".").pop().toLowerCase();
      if (!["xlsx", "xls", "csv"].includes(fileExtension)) {
        this.parseError = "Invalid file format. Please upload an Excel (.xlsx, .xls) or CSV file.";
        this.ShowAlertError(this.parseError, "");
        return;
      }

      const reader = new FileReader();
      reader.onload = e => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array", cellDates: true });

          if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
            throw new Error("The uploaded Excel workbook contains no sheets.");
          }

          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const rawRecords = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

          if (!rawRecords || rawRecords.length === 0) {
            this.parseError = "The selected file is empty or contains no data rows.";
            this.ShowAlertWarning("Empty File", this.parseError);
            return;
          }

          this.mapImportedRecords(rawRecords);
          this.ShowAlertSuccess(`Successfully extracted ${rawRecords.length} records.`, "");
        } catch (err) {
          this.parseError = err.message || "Failed to parse the Excel file.";
          this.ShowAlertError("File Parse Error", this.parseError);
        }
      };

      reader.onerror = () => {
        this.parseError = "Failed to read file from disk.";
        this.ShowAlertError("Read Error", this.parseError);
      };

      reader.readAsArrayBuffer(file);
    },

    normalizeKey(key) {
      return String(key || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    },

    findValueBySynonyms(obj, synonyms) {
      const normalizedKeys = Object.keys(obj).reduce((acc, k) => {
        acc[this.normalizeKey(k)] = obj[k];
        return acc;
      }, {});

      for (const syn of synonyms) {
        const normSyn = this.normalizeKey(syn);
        if (normalizedKeys[normSyn] !== undefined && normalizedKeys[normSyn] !== "") {
          return normalizedKeys[normSyn];
        }
      }
      return "";
    },

    parseSex(rawVal) {
      if (!rawVal) return "0";
      const s = String(rawVal).trim().toLowerCase();
      if (s === "10" || s === "m" || s === "male" || s === "boy") return "10";
      if (s === "11" || s === "f" || s === "female" || s === "girl") return "11";
      return "0";
    },

    parseDate(rawVal) {
      if (!rawVal) return "";
      if (rawVal instanceof Date && !isNaN(rawVal.getTime())) {
        return moment(rawVal).format("YYYY-MM-DD");
      }
      const str = String(rawVal).trim();
      const parsed = moment(str, ["YYYY-MM-DD", "DD/MM/YYYY", "MM/DD/YYYY", "DD-MM-YYYY", "YYYY/MM/DD"], true);
      if (parsed.isValid()) {
        return parsed.format("YYYY-MM-DD");
      }
      const loose = moment(str);
      return loose.isValid() ? loose.format("YYYY-MM-DD") : "";
    },

    mapImportedRecords(records) {
      const mapped = records.map(record => {
        this.rowCounter += 1;
        const admissionNo = String(
          this.findValueBySynonyms(record, [
            "admissionno", "admissionnumber", "admno", "admission_no", "regno", "studentno", "studentnumber"
          ])
        ).trim();

        const lastName = String(
          this.findValueBySynonyms(record, [
            "surname", "lastname", "last_name", "sur_name", "familyname"
          ])
        ).trim();

        const firstName = String(
          this.findValueBySynonyms(record, [
            "firstname", "first_name", "givenname"
          ])
        ).trim();

        const middleName = String(
          this.findValueBySynonyms(record, [
            "othername", "middlename", "middle_name", "other_name", "othernames"
          ])
        ).trim();

        const sexRaw = this.findValueBySynonyms(record, ["sex", "gender", "sexid"]);
        const sexId = this.parseSex(sexRaw);

        const dobRaw = this.findValueBySynonyms(record, ["dob", "dateofbirth", "date_of_birth", "birthdate"]);
        const dob = this.parseDate(dobRaw);

        const admDateRaw = this.findValueBySynonyms(record, ["admissiondate", "admission_date", "dateofadmission", "entrydate"]);
        const admissionDate = this.parseDate(admDateRaw) || moment().format("YYYY-MM-DD");

        const gLastName = String(
          this.findValueBySynonyms(record, ["guardiansurname", "guardian_surname", "parent_surname", "parentsurname"])
        ).trim();

        const gFirstName = String(
          this.findValueBySynonyms(record, ["guardianfirstname", "guardian_firstname", "parent_firstname", "parentfirstname"])
        ).trim();

        const gMiddleName = String(
          this.findValueBySynonyms(record, ["guardianothername", "guardianmiddlename", "guardian_middlename", "parent_othername"])
        ).trim();

        const gPhoneNo = String(
          this.findValueBySynonyms(record, ["guardianphone", "guardianphoneno", "guardian_phone", "guardianphonenumber", "parentphone", "phonenumber", "phone"])
        ).trim();

        const gEmail = String(
          this.findValueBySynonyms(record, ["guardianemail", "guardian_email", "parentemail", "email", "parent_email"])
        ).trim();

        const fullAddress = String(
          this.findValueBySynonyms(record, ["fulladdress", "address", "homeaddress", "residentialaddress"])
        ).trim();

        return {
          _rowId: `row-${Date.now()}-${this.rowCounter}`,
          AdmissionNo: admissionNo,
          LastName: lastName,
          FirstName: firstName,
          MiddleName: middleName,
          SexId: sexId,
          DOB: dob,
          Admissiondate: admissionDate,
          GLastName: gLastName,
          GFirstname: gFirstName,
          GMiddlename: gMiddleName,
          GPhoneNo: gPhoneNo,
          GEmail: gEmail,
          FullAddress: fullAddress,
          status: "pending",
          errorMsg: "",
          _errors: []
        };
      });

      this.rows = mapped;
      this.hasRunOnce = false;
      this.validateAllBulkRows();
    },

    validateAllBulkRows() {
      const seenInFile = {};
      const existingAdmissions = new Set(
        (this.existingStudents || [])
          .map(s => String(s.admNo || s.AdmissionNo || "").trim().toLowerCase())
          .filter(Boolean)
      );

      this.rows.forEach(r => {
        const adm = (r.AdmissionNo || "").trim().toLowerCase();
        if (adm) {
          seenInFile[adm] = (seenInFile[adm] || 0) + 1;
        }
      });

      this.rows.forEach(row => {
        const errors = [];
        const admKey = (row.AdmissionNo || "").trim().toLowerCase();

        if (!row.AdmissionNo || !row.AdmissionNo.trim()) {
          errors.push("Admission number is required");
        } else if (seenInFile[admKey] > 1) {
          errors.push("Duplicate admission number within uploaded file");
        } else if (existingAdmissions.has(admKey) && row.status !== "success") {
          errors.push("Admission number already exists in school database");
        }

        if (!row.LastName || !row.LastName.trim()) {
          errors.push("Surname is required");
        }
        if (!row.FirstName || !row.FirstName.trim()) {
          errors.push("First name is required");
        }
        if (!row.SexId || row.SexId === "0" || row.SexId === 0) {
          errors.push("Sex selection is required");
        }
        if (!row.GPhoneNo || !row.GPhoneNo.trim()) {
          errors.push("Guardian phone number is required");
        }
        if (!row.FullAddress || !row.FullAddress.trim()) {
          errors.push("Full address is required");
        }

        row._errors = errors;
      });
    },

    fieldHasError(row, fieldName) {
      if (!row._errors || row._errors.length === 0) return false;
      if (fieldName === "AdmissionNo") {
        return row._errors.some(e => e.toLowerCase().includes("admission"));
      }
      if (fieldName === "LastName") {
        return row._errors.some(e => e.toLowerCase().includes("surname"));
      }
      if (fieldName === "FirstName") {
        return row._errors.some(e => e.toLowerCase().includes("first name"));
      }
      if (fieldName === "SexId") {
        return row._errors.some(e => e.toLowerCase().includes("sex"));
      }
      if (fieldName === "GPhoneNo") {
        return row._errors.some(e => e.toLowerCase().includes("guardian phone"));
      }
      if (fieldName === "FullAddress") {
        return row._errors.some(e => e.toLowerCase().includes("address"));
      }
      return false;
    },

    addNewRow() {
      this.rowCounter += 1;
      this.rows.unshift({
        _rowId: `row-${Date.now()}-${this.rowCounter}`,
        AdmissionNo: "",
        LastName: "",
        FirstName: "",
        MiddleName: "",
        SexId: "0",
        DOB: "",
        Admissiondate: moment().format("YYYY-MM-DD"),
        GLastName: "",
        GFirstname: "",
        GMiddlename: "",
        GPhoneNo: "",
        GEmail: "",
        FullAddress: "",
        status: "pending",
        errorMsg: "",
        _errors: []
      });
      this.validateAllBulkRows();
    },

    removeRow(index) {
      if (index >= 0 && index < this.rows.length) {
        this.rows.splice(index, 1);
        this.validateAllBulkRows();
      }
    },

    resetBulkUpload() {
      this.rows = [];
      this.selectedFileName = "";
      this.parseError = "";
      this.processing = false;
      this.hasRunOnce = false;
      this.currentIndex = -1;
      if (this.$refs.excelUploadComp && this.$refs.excelUploadComp.clear) {
        this.$refs.excelUploadComp.clear();
      } else if (this.$refs.excelFileInput) {
        this.$refs.excelFileInput.value = "";
      }
    },

    async startBulkSubmission() {
      if (!this.isClassOptionArmSelected) {
        this.ShowAlertWarning("Selection Required", "Please select Class, Option, and Arm first.");
        return;
      }

      this.validateAllBulkRows();
      if (this.hasValidationErrors) {
        this.ShowAlertWarning(
          "Validation Errors Found",
          `Please fix the ${this.totalErrorCount} error(s) highlighted in red before submitting.`
        );
        return;
      }

      const pendingRows = this.rows.filter(r => r.status !== "success");
      if (pendingRows.length === 0) {
        this.ShowAlertSuccess("All records have already been processed successfully.", "");
        return;
      }

      this.processing = true;
      this.hasRunOnce = true;

      const username = this.$store.state.auth.username;
      const accesskey = this.$store.state.auth.accesskey;

      for (let i = 0; i < this.rows.length; i++) {
        const row = this.rows[i];
        if (row.status === "success") continue;

        this.currentIndex = i;
        row.status = "processing";
        row.errorMsg = "";

        const payload = {
          Id: 0,
          UserId: 0,
          Passport: "",
          ContactId: 1,
          GContactId: 0,
          GUserId: 0,
          GPhoneNo: row.GPhoneNo,
          GTitle: "",
          GFirstname: row.GFirstname || "",
          GMiddlename: row.GMiddlename || "",
          GLastName: row.GLastName || "",
          GEmail: row.GEmail || "",
          GId: 0,
          FullAddress: row.FullAddress,
          FirstName: row.FirstName,
          AdmissionNo: row.AdmissionNo,
          Admissiondate: row.Admissiondate || moment().format("YYYY-MM-DD"),
          // Inherit the single session selected Class, Option, and Arm
          ClassId: this.selectedClassId,
          OptionId: this.selectedOptionId,
          ArmId: this.selectedArmId,
          MiddleName: row.MiddleName || "",
          LastName: row.LastName,
          SexId: row.SexId,
          DOB: row.DOB || "",
          processop: "N",
          username,
          accesskey
        };

        try {
          const saved = await this.studentRepository.create(payload);
          row.status = "success";
          row.errorMsg = "";
          row._errors = [];
          this.$emit("student-created", saved);
        } catch (err) {
          row.status = "failed";
          row.errorMsg = (err && (err.responseMessage || err.message)) || "Submission failed";
        }

        // Small interval yield to ensure UI responsiveness and clear progression
        await new Promise(resolve => setTimeout(resolve, 80));
      }

      this.processing = false;
      this.currentIndex = -1;

      if (this.failedCount === 0) {
        this.ShowAlertSuccess(
          "Bulk Import Complete",
          `All ${this.successCount} student records were successfully submitted to ${this.selectedClassLabel} (${this.selectedOptionLabel} - ${this.selectedArmLabel}) and queued for synchronization.`
        );
      } else {
        this.ShowAlertWarning(
          "Bulk Import Finished with Errors",
          `${this.successCount} record(s) uploaded successfully. ${this.failedCount} record(s) failed. You can review and retry failed records.`
        );
      }
    },

    retryFailed() {
      this.rows.forEach(r => {
        if (r.status === "failed") {
          r.status = "pending";
          r.errorMsg = "";
        }
      });
      this.startBulkSubmission();
    },

    downloadTemplate() {
      const templateData = [
        {
          "Admission Number": "SD/2026/001",
          "Surname": "Ogunleye",
          "First Name": "Adedamola",
          "Other Name": "Emmanuel",
          "Sex": "Male",
          "Date of Birth": "2012-05-14",
          "Guardian Surname": "Ogunleye",
          "Guardian First Name": "Babatunde",
          "Guardian Other Name": "Michael",
          "Guardian Phone": "08012345678",
          "Guardian Email": "parent@example.com",
          "Full Address": "12 Innovation Way, Lagos"
        },
        {
          "Admission Number": "SD/2026/002",
          "Surname": "Chukwu",
          "First Name": "Chiamaka",
          "Other Name": "Grace",
          "Sex": "Female",
          "Date of Birth": "2013-08-22",
          "Guardian Surname": "Chukwu",
          "Guardian First Name": "Emeka",
          "Guardian Other Name": "Paul",
          "Guardian Phone": "08098765432",
          "Guardian Email": "chukwu.parent@example.com",
          "Full Address": "45 Freedom Street, Abuja"
        }
      ];

      const ws = XLSX.utils.json_to_sheet(templateData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Students_Template");
      XLSX.writeFile(wb, "Student_Bulk_Upload_Template.xlsx");
    },

    onResize() {
      if (typeof window !== "undefined") {
        this.windowWidth = window.innerWidth;
      }
    },

    cancel() {
      this.$emit("close");
    }
  },
  mounted() {
    if (typeof window !== "undefined") {
      window.addEventListener("resize", this.onResize);
    }
  },
  beforeDestroy() {
    if (typeof window !== "undefined") {
      window.removeEventListener("resize", this.onResize);
    }
  }
};
</script>

<style scoped>
.unified-student-upload {
  position: relative;
}

.bulk-table-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.bulk-table {
  font-size: 13px;
  min-width: 1400px;
}

.bulk-table thead th {
  background-color: #f8fafc;
  font-weight: 700;
  text-transform: uppercase;
  font-size: 11px;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #e2e8f0;
  padding: 0.55rem 0.5rem;
}

.bulk-table td {
  padding: 0.4rem 0.5rem;
}

.table-danger-light {
  background-color: rgba(254, 226, 226, 0.45) !important;
}

.table-success-light {
  background-color: rgba(220, 252, 231, 0.45) !important;
}

.table-info-light {
  background-color: rgba(224, 242, 254, 0.55) !important;
}

.custom-file-label::after {
  content: "Browse";
  background-color: #5e72e4;
  color: #fff;
}

/* Mobile card list styling */
.mobile-cards-container {
  background-color: #f8fafc;
  border-radius: 6px;
}

.mobile-record-card {
  border-radius: 8px;
  background: #ffffff;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.mobile-record-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
}

.mobile-record-card .form-control-label {
  font-size: 11px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}
</style>
